import React from 'react';
import { Shield, RefreshCw, Bot, Zap, RotateCw, Database, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { Neo4jStatsResponse } from '@/services/kbService';
import { formatDate } from './qaUtils';

export interface QAPageHeaderProps {
  neo4jStats: Neo4jStatsResponse | null;
  aiAnalyzingMode: 'analyze' | 'heal' | null;
  rebuildingGraph: boolean;
  dashboardLoading: boolean;
  autoFixing?: boolean;
  onAIAnalyze: (autoApply: boolean) => void;
  onRebuildGraph: () => void;
  onRefreshAll: () => void;
  onAutoFix?: () => void;
}

export const QAPageHeader: React.FC<QAPageHeaderProps> = ({
  neo4jStats,
  aiAnalyzingMode,
  rebuildingGraph,
  dashboardLoading,
  autoFixing,
  onAIAnalyze,
  onRebuildGraph,
  onRefreshAll,
  onAutoFix,
}) => {
  return (
    <Card className="border-0 bg-transparent shadow-none">
      <CardContent className="p-0">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-primary flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold">Entity Quality Assurance</h1>
              <p className="text-xs text-muted-foreground hidden sm:block">
                Monitor and improve entity extraction quality across the knowledge base
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-2 w-full sm:w-auto">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs sm:text-sm whitespace-nowrap w-full sm:w-auto"
                    onClick={() => onAIAnalyze(false)}
                    disabled={aiAnalyzingMode === 'analyze'}
                  >
                    {aiAnalyzingMode === 'analyze' ? <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" /> : <Bot className="h-3 w-3 sm:h-4 sm:w-4" />}
                   Analyze
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="dark">Run AI analysis to detect misclassified entities</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="text-xs sm:text-sm whitespace-nowrap w-full sm:w-auto"
                    onClick={() => onAIAnalyze(true)}
                    disabled={aiAnalyzingMode === 'heal'}
                  >
                    {aiAnalyzingMode === 'heal' ? <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" /> : <Zap className="h-3 w-3 sm:h-4 sm:w-4" />}
                    Heal
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="dark">Run AI analysis and auto-apply high-confidence corrections</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {onAutoFix && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs sm:text-sm whitespace-nowrap w-full sm:w-auto border-amber-500/40 text-amber-500 hover:bg-amber-500/10"
                      onClick={onAutoFix}
                      disabled={autoFixing}
                    >
                      {autoFixing ? <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" /> : <Wrench className="h-3 w-3 sm:h-4 sm:w-4" />}
                      Auto-Fix
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="dark">Auto-delete noise, merge duplicates, dismiss orphans</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    className="text-xs sm:text-sm whitespace-nowrap w-full sm:w-auto"
                    onClick={onRebuildGraph}
                    disabled={rebuildingGraph}
                  >
                    {rebuildingGraph ? <RotateCw className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" /> : <RotateCw className="h-3 w-3 sm:h-4 sm:w-4" />}
                    Sync Knowledge
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="dark">
                  {neo4jStats?.is_connected
                    ? `Neo4j: ${neo4jStats.total_nodes} nodes, ${neo4jStats.total_relationships} relationships`
                    : 'Neo4j not connected'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <Button
              variant="outline"
              size="sm"
              className="text-xs sm:text-sm whitespace-nowrap w-full sm:w-auto col-span-2 sm:col-span-1"
              onClick={onRefreshAll}
              disabled={dashboardLoading}
            >
              {dashboardLoading ? <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" /> : <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4" />}
              Refresh All
            </Button>
          </div>
        </div>

        {/* Neo4j Stats Summary */}
        {neo4jStats && neo4jStats.is_connected && (
          <div className="mt-2 flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Database className="h-3 w-3 sm:h-4 sm:w-4" />
              Neo4j: {neo4jStats.total_nodes} nodes
            </span>
            <Separator orientation="vertical" className="h-3 sm:h-4" />
            <span>{neo4jStats.total_relationships} relationships</span>
            {neo4jStats.last_sync_at && (
              <>
                <Separator orientation="vertical" className="h-3 sm:h-4 hidden sm:block" />
                <span className="hidden sm:inline">Last sync: {formatDate(neo4jStats.last_sync_at)}</span>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
